import { isMapAlive } from '../Map/utils/canAccessMapStyle';
import { get } from '../services/httpClient';

// Cache de signed URLs para no regenerar en cada move
// key: s3_key → { signedUrl, expiresAt }
const signedUrlCache = new Map();

/** Timeout corto: si Gateway/Lambda no responden, el mapa no puede quedar colgado. */
const SIGN_TIMEOUT_MS = 8000;
/** Un reintento por token que todavía no hidrató, o un timeout transitorio. */
const SIGN_RETRY_DELAY_MS = 500;
const SIGN_MAX_ATTEMPTS = 2;

/**
 * Extrae el S3 key desde una URL pmtiles://https://s3...
 * Path-style:
 *   pmtiles://https://s3.us-east-1.amazonaws.com/argenlot.lots/tiles/cordoba/low/cordoba_low.pmtiles
 *   → tiles/cordoba/low/cordoba_low.pmtiles
 * Virtual-hosted (bucket puede tener puntos, p. ej. argenlot.lots):
 *   pmtiles://https://argenlot.lots.s3.us-east-1.amazonaws.com/tiles/cordoba/points/foo.pmtiles
 *   → tiles/cordoba/points/foo.pmtiles
 */
function extractS3Key(pmtilesUrl) {
  try {
    const httpsUrl = pmtilesUrl.replace('pmtiles://', '');
    const url = new URL(httpsUrl);
    const parts = url.pathname.split('/').filter(Boolean);
    if (parts.length === 0) return null;

    // bucket.s3.amazonaws.com | bucket.s3.region.amazonaws.com | bucket.s3-region.amazonaws.com
    const isVirtualHosted = /^.+\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i.test(url.hostname);
    if (isVirtualHosted) {
      return parts.join('/');
    }

    // Path-style: primer segmento es el bucket.
    parts.shift();
    return parts.join('/');
  } catch {
    return null;
  }
}

function readSignedPayload(data) {
  if (data?.signedUrl) {
    return { signedUrl: data.signedUrl, expiresIn: data.expiresIn };
  }
  if (typeof data?.body === 'string') {
    try {
      return JSON.parse(data.body);
    } catch {
      return null;
    }
  }
  if (data?.body && typeof data.body === 'object') {
    return data.body;
  }
  return null;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Margen antes del vencimiento para renovar (alineado con el cache hit de getSignedPmtilesUrl). */
const REFRESH_BUFFER_MS = 60_000;

const refreshInFlight = new Map();

/**
 * Indica si la firma en cache está por vencer o ya venció (hay que llamar setUrl en el source).
 */
export function pmtilesSignedUrlNeedsRefresh(originalUrl, bufferMs = REFRESH_BUFFER_MS) {
  const s3Key = extractS3Key(originalUrl);
  if (!s3Key) return false;
  const cached = signedUrlCache.get(s3Key);
  if (!cached) return false;
  return cached.expiresAt - Date.now() <= bufferMs;
}

/**
 * Obtiene una Signed URL válida para el archivo PMTiles.
 * Usa cache para evitar llamadas repetidas mientras no expire.
 * Si la firma falla, **no** devuelve la URL cruda de S3 (da 403 y el source queda muerto).
 *
 * @param {string} originalUrl URL con esquema pmtiles:// apuntando al bucket
 * @param {{ forceRefresh?: boolean }} [options]
 * @returns {Promise<string|null>} `pmtiles://` firmada, la URL original si no es S3, o null si falló
 */
export async function getSignedPmtilesUrl(originalUrl, { forceRefresh = false } = {}) {
  const s3Key = extractS3Key(originalUrl);
  if (!s3Key) return originalUrl;

  const cached = signedUrlCache.get(s3Key);
  const now = Date.now();

  if (!forceRefresh && cached && cached.expiresAt - now > REFRESH_BUFFER_MS) {
    return `pmtiles://${cached.signedUrl}`;
  }

  const apiKey = process.env.REACT_APP_PMTILES_API_KEY;
  let lastError = null;

  for (let attempt = 1; attempt <= SIGN_MAX_ATTEMPTS; attempt += 1) {
    if (attempt > 1) {
      await delay(SIGN_RETRY_DELAY_MS);
    }

    try {
      // API Gateway: authorizer CUSTOM por x-api-key (401 sin key, 403 key
      // inválida); el Bearer no cuenta. Sin refresh de token ante 401.
      const data = await get('/getpmtilesurl', {
        params: { key: s3Key },
        auth: true,
        retryOn401: false,
        timeoutMs: SIGN_TIMEOUT_MS,
        headers: apiKey ? { 'x-api-key': apiKey } : undefined,
      });

      if (!data._httpOk) {
        lastError = new Error(`Error ${data._httpStatus}`);
        if (data._httpStatus === 400) break;
        continue;
      }

      const payload = readSignedPayload(data);
      if (!payload?.signedUrl) {
        lastError = new Error('Respuesta sin signedUrl');
        continue;
      }

      const expiresIn = Number(payload.expiresIn) > 0 ? Number(payload.expiresIn) : 300;
      signedUrlCache.set(s3Key, {
        signedUrl: payload.signedUrl,
        expiresAt: Date.now() + expiresIn * 1000,
      });

      return `pmtiles://${payload.signedUrl}`;
    } catch (error) {
      lastError = error;
    }
  }

  console.error('Error obteniendo Signed URL para PMTiles:', lastError);
  // Una firma vencida o por vencer sigue siendo mejor que pegarle a S3 sin query.
  if (cached?.signedUrl) return `pmtiles://${cached.signedUrl}`;
  return null;
}

/**
 * Renueva la URL del source vectorial si la firma en cache está vencida o por vencer.
 * Evita peticiones duplicadas por sourceId mientras hay un refresh en curso.
 */
export async function refreshPmtilesVectorSourceIfNeeded(map, sourceId, originalUrl) {
  if (!map || !originalUrl || !pmtilesSignedUrlNeedsRefresh(originalUrl)) return;

  if (refreshInFlight.has(sourceId)) {
    return refreshInFlight.get(sourceId);
  }

  const run = (async () => {
    try {
      const before = map.getSource(sourceId);
      if (!before || typeof before.setUrl !== 'function') return;

      const nextUrl = await getSignedPmtilesUrl(originalUrl, { forceRefresh: true });
      if (!nextUrl) return;
      // La firma es async: si el mapa se destruyó, no tocarlo.
      if (!isMapAlive(map)) return;
      const after = map.getSource(sourceId);
      if (!after || typeof after.setUrl !== 'function') return;
      after.setUrl(nextUrl);
    } catch (error) {
      console.error('Error renovando URL firmada PMTiles:', error);
    } finally {
      refreshInFlight.delete(sourceId);
    }
  })();

  refreshInFlight.set(sourceId, run);
  return run;
}
